import React, { ReactElement, useState } from 'react';
import classNames from 'classnames';

import type { ReviewListFilter } from 'modules/review/review.interface';
import { ReviewRatingFilters } from 'modules/review/review.enum';
import DynamicLink from 'components/dynamic-link/dynamic-link';
import { ReviewQueryParams, ReviewQueryRatingValues } from 'modules/review/review-query-params.enum';

import './review-rating-filter.scss';
import { ReviewListRatingStatsProp } from 'components/review/review.interface';
import { currency, numeralForm } from 'helpers/number';

export const ReviewRatingBreakpoints = {
    positive: 3.25,
    negative: 2.25
};

interface ReviewFilterProps {
    handleFilter: (value: ReviewListFilter) => void;
    activeFilter?: ReviewRatingFilters;
    type: 'company' | 'product';
    ratingStats?: ReviewListRatingStatsProp;
}

const ReviewRatingFilter: React.FC<ReviewFilterProps> = ({
    type,
    handleFilter,
    activeFilter: filterValue,
    ratingStats
}): ReactElement => {
    const [activeFilter, setActiveFilter] = useState(filterValue || ReviewRatingFilters.ALL);

    const handleClick = (value: ReviewRatingFilters): void => {
        if (activeFilter !== value) {
            handleFilter({ rating: value });
            setActiveFilter(value);
        }
    };

    return (
        <div className="review-rating-filters">
            <DynamicLink
                locationParams={{ query: {
                    [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : null,
                    [ReviewQueryParams.rating]: null,
                    [ReviewQueryParams.pagination]: null
                } }}
                className={classNames({
                    'review-rating-filters__button': true,
                    'review-rating-filters__button--active': ReviewRatingFilters.ALL === activeFilter
                })}
                onClick={(): void => handleClick(ReviewRatingFilters.ALL)}
            >
                <span className="review-rating-filters__button-label">
                    Wszystkie
                </span>
                {ratingStats && (
                    <span className="review-rating-filters__button-counter">
                        {currency(ratingStats.all)}
                        {' '}
                        {numeralForm(ratingStats.all, 'opinia', 'opinie', 'opinii')}
                    </span>
                )}
            </DynamicLink>
            <DynamicLink
                locationParams={{ query: {
                    [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : null,
                    [ReviewQueryParams.rating]: ReviewQueryRatingValues.negative,
                    [ReviewQueryParams.pagination]: null
                } }}
                className={classNames({
                    'review-rating-filters__button': true,
                    'review-rating-filters__button--active': ReviewRatingFilters.NEGATIVE === activeFilter
                })}
                onClick={(): void => handleClick(ReviewRatingFilters.NEGATIVE)}
            >
                <span className="review-rating-filters__button-label">
                    Negatywne
                </span>
                {ratingStats && (
                    <span className="review-rating-filters__button-counter">
                        {currency(ratingStats.negative)}
                        {' '}
                        {numeralForm(ratingStats.negative, 'opinia', 'opinie', 'opinii')}
                    </span>
                )}
            </DynamicLink>
            <DynamicLink
                locationParams={{ query: {
                    [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : null,
                    [ReviewQueryParams.rating]: ReviewQueryRatingValues.neutral,
                    [ReviewQueryParams.pagination]: null
                } }}
                className={classNames({
                    'review-rating-filters__button': true,
                    'review-rating-filters__button--active': ReviewRatingFilters.NEUTRAL === activeFilter
                })}
                onClick={(): void => handleClick(ReviewRatingFilters.NEUTRAL)}
            >
                <span className="review-rating-filters__button-label">
                    Neutralne
                </span>
                {ratingStats && (
                    <span className="review-rating-filters__button-counter">
                        {currency(ratingStats.neutral)}
                        {' '}
                        {numeralForm(ratingStats.neutral, 'opinia', 'opinie', 'opinii')}
                    </span>
                )}
            </DynamicLink>
            <DynamicLink
                locationParams={{ query: {
                    [ReviewQueryParams.type]: type === 'product' ? 'produktowe' : null,
                    [ReviewQueryParams.rating]: ReviewQueryRatingValues.positive,
                    [ReviewQueryParams.pagination]: null
                } }}
                className={classNames({
                    'review-rating-filters__button': true,
                    'review-rating-filters__button--active': ReviewRatingFilters.POSITIVE === activeFilter
                })}
                onClick={(): void => handleClick(ReviewRatingFilters.POSITIVE)}
            >
                <span className="review-rating-filters__button-label">
                    Pozytywne
                </span>
                {ratingStats && (
                    <span className="review-rating-filters__button-counter">
                        {currency(ratingStats.positive)}
                        {' '}
                        {numeralForm(ratingStats.positive, 'opinia', 'opinie', 'opinii')}
                    </span>
                )}
            </DynamicLink>
            {type === 'product' && (
                <DynamicLink
                    locationParams={{ query: {
                        [ReviewQueryParams.type]: 'produktowe',
                        [ReviewQueryParams.rating]: ReviewQueryRatingValues.credible,
                        [ReviewQueryParams.pagination]: null
                    } }}
                    className={classNames({
                        'review-rating-filters__button': true,
                        'review-rating-filters__button--active': ReviewRatingFilters.CREDIBLE === activeFilter
                    })}
                    onClick={(): void => handleClick(ReviewRatingFilters.CREDIBLE)}
                >
                    <span className="review-rating-filters__button-label">
                        Potwierdzone zakupem
                    </span>
                </DynamicLink>
            )}
        </div>
    );
};

export default ReviewRatingFilter;
